import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  IconButton,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Slide,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircleOutline, Badge as BadgeIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import AuthService from '../services/authService';
import logoImage from '../assets/logo.png';
import hakikiColors from '../theme/hakikiColors';
import { loginBgStyles } from './loginPageBgStyles';
import api from '../services/api';
import { t } from '../utils/textUtils';
import { getRoleLabel, getRoleColor } from '../utils/roleConfig';

// Role label & color come from the shared roleConfig (single source of truth).
const roleLabel = (r) => getRoleLabel(r).toUpperCase();
const roleColor = (r) => getRoleColor(r);
const roleInitial = (r) => roleLabel(r).charAt(0);

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Login = () => {
  const navigate = useNavigate();
  const { updateUserContext, error: authError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  
  const { register, handleSubmit, getValues, formState: { errors } } = useForm({
    defaultValues: {
      username: '',
      password: '',
    }
  });

  // Role selection dialog state (shown when account has multiple roles)
  const [roleDialog, setRoleDialog] = useState({ open: false, roles: [] });
  // Welcome dialog state (shown after successful login)
  const [welcomeDialog, setWelcomeDialog] = useState({ open: false, user: null });

  // Auto-continue from welcome dialog after a short delay for smooth UX.
  useEffect(() => {
    if (!welcomeDialog.open) return undefined;
    const timer = setTimeout(() => {
      handleWelcomeContinue();
    }, 3500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [welcomeDialog.open]);
  
  // Menampilkan pesan error dari AuthContext
  useEffect(() => {
    if (authError) {
      toast.error(authError);
    }
  }, [authError]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const performLogin = async (credentials) => {
    // Call AuthService directly instead of AuthContext.login so that setting
    // `user` on the context (which triggers PublicRoute redirect) is deferred
    // until the welcome dialog is acknowledged.
    const response = await AuthService.login(credentials);

    if (response && response.multipleRoles) {
      // No token is issued for multi-role responses — safe to show picker.
      setRoleDialog({ open: true, roles: response.availableRoles || [] });
      return;
    }

    if (response && response.user && response.token) {
      // Token + user were already persisted by AuthService. We intentionally
      // do NOT call updateUserContext yet — doing so would flip
      // isAuthenticated and PublicRoute would redirect away from this page
      // before the welcome dialog can render. It will be called on continue.
      setWelcomeDialog({ open: true, user: response.user });
      return;
    }

    throw new Error(t('LOGIN FAILED. PLEASE CHECK YOUR CREDENTIALS.'));
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await performLogin({
        username: data.username,
        password: data.password,
      });
    } catch (error) {
      toast.error(error.message || t('LOGIN FAILED. PLEASE CHECK YOUR CREDENTIALS.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRole = async (selectedRole) => {
    const { username, password } = getValues();
    setRoleDialog({ open: false, roles: [] });
    setLoading(true);
    try {
      await performLogin({ username, password, role: selectedRole });
    } catch (error) {
      toast.error(error.message || t('LOGIN FAILED. PLEASE CHECK YOUR CREDENTIALS.'));
    } finally {
      setLoading(false);
    }
  };

  const handleWelcomeContinue = () => {
    const u = welcomeDialog.user;
    setWelcomeDialog({ open: false, user: null });
    // Promote user into auth context now so protected routes become accessible.
    if (u) updateUserContext(u);
    // Return the user to where they were when their session expired, if any.
    let target = '/dashboard';
    try {
      const saved = sessionStorage.getItem('postLoginRedirect');
      sessionStorage.removeItem('postLoginRedirect');
      if (saved && saved.startsWith('/') && !saved.startsWith('//') && saved !== '/login') {
        target = saved;
      }
    } catch (e) { /* storage unavailable */ }
    navigate(target, { replace: true });
  };

  // Google OAuth functionality has been removed

  // const handleForgotPassword = () => {
  //   setIsResetMode(true);
  // };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error(t('SILAKAN MASUKKAN EMAIL ANDA'));
      return;
    }

    setLoading(true);
    try {
      // Panggil API reset password
      const response = await api.post('/auth/forgot-password', { email: resetEmail });
      setResetSuccess(true);
      toast.success(response.data.message || t('INSTRUKSI RESET PASSWORD TELAH DIKIRIM KE EMAIL ANDA'));
      
      // Untuk pengembangan, tampilkan token (dalam produksi, hapus ini)
      if (response.data.resetToken) {
        console.log('Reset Token:', response.data.resetToken);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('GAGAL MENGIRIM PERMINTAAN RESET PASSWORD'));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setIsResetMode(false);
    setResetSuccess(false);
    setResetEmail('');
  };

  // Render form reset password jika dalam mode reset
  if (isResetMode) {
    return (
      <Container component="main" maxWidth="xs">
        <Box 
          sx={{ 
            mt: 8, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
          }}
        >
          {/* Logo dan Judul */}
          <Box sx={{ mb: 4, textAlign: 'center', margin: '0 auto' }}>
            <img src={logoImage} alt="Hakiki Steel Logo" style={{ maxWidth: '100%', height: 'auto', marginBottom: '10px' }} />
          </Box>
          
          <Typography component="h1" variant="h3" sx={{ mb: 4, fontWeight: 500, textAlign: 'center' }}>
            {resetSuccess ? t('EMAIL TERKIRIM') : t('LUPA PASSWORD')}
          </Typography>
          
          {resetSuccess ? (
            <Box sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {t('INSTRUKSI RESET PASSWORD TELAH DIKIRIM KE EMAIL ANDA. SILAKAN PERIKSA KOTAK MASUK ANDA.')}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={handleBackToLogin}
                sx={{ 
                  mt: 2, 
                  py: 1.5,
                  backgroundColor: '#F0C14B',
                  '&:hover': {
                    backgroundColor: '#DBA800',
                  }
                }}
              >
                Kembali ke Login
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleResetPassword} sx={{ width: '100%' }}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {t('MASUKAN ALAMAT EMAIL ANDA DAN KAMI AKAN MENGIRIMKAN INSTRUKSI UNTUK MERESET PASSWORD ANDA.')}
              </Typography>
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label={t('EMAIL')}
                name="email"
                autoComplete="email"
                autoFocus
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ 
                  mt: 3, 
                  mb: 2,
                  py: 1.5,
                  backgroundColor: '#F0C14B',
                  '&:hover': {
                    backgroundColor: '#DBA800',
                  }
                }}
              >
                {loading ? <CircularProgress size={24} /> : t('KIRIM INSTRUKSI RESET')}
              </Button>
              
              <Button
                fullWidth
                variant="outlined"
                onClick={handleBackToLogin}
                sx={{ 
                  py: 1.5,
                  borderColor: '#F0C14B',
                  color: '#5D4037',
                  '&:hover': {
                    borderColor: '#DBA800',
                    backgroundColor: 'rgba(240, 193, 75, 0.04)',
                  }
                }}
              >
                Kembali ke Login
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={loginBgStyles}>
      <Box sx={{
        display: 'flex',
        width: { xs: '92%', md: '450px' },
        height: { xs: '100%', md: '530px' },
        boxShadow: '0 8px 32px rgba(200,164,21,0.08), 0 1.5px 8px rgba(0, 0, 0, 0.07)',
        borderRadius: 4,
        overflow: 'hidden',
      }}>
        {/* Left: Login Form */}
        <Box sx={{
          flex: 1,
          p: { xs: 3, md: 5, lg: 6 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          background: '#fff',
          textAlign: 'center',
        }}>
          {/* Logo and Title */}
          <Box sx={{ mb: 2, mt: 0, display: 'flex', alignItems: 'center', gap: 1, textAlign: 'center' }}>
            <img src={logoImage} alt="Hakiki Logo" style={{ maxWidth: '70%', height: 'auto', margin: '0 auto' }} />
          </Box>
          {/* Welcome Text */}
          <Typography variant="body1" sx={{ color: 'grey.600', mb: 1, mt: 2 }}>
            {t('MASUKAN DETAIL LOGIN ANDA DI BAWAH INI')}
          </Typography>
          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              label={t('EMAIL / NAMA PENGGUNA')}
              placeholder={t('MASUKKAN EMAIL')}
              name="username"
              autoComplete="username"
              autoFocus
              fullWidth
              margin="normal"
              inputProps={{ 'data-testid': 'email-input' }}
              {...register('username', { required: t('EMAIL HARUS DIISI') })}
              error={!!errors.username}
              helperText={errors.username?.message}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '&.Mui-focused fieldset': {
                    borderColor: hakikiColors.main,
                  },
                },
                '& label.Mui-focused': {
                  color: hakikiColors.main,
                },
              }}
            />
            <TextField
              label={t('PASSWORD')}
              placeholder={t('MASUKKAN PASSWORD')}
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              fullWidth
              margin="normal"
              inputProps={{ 'data-testid': 'password-input' }}
              {...register('password', { required: t('PASSWORD HARUS DIISI') })}
              error={!!errors.password}
              helperText={errors.password?.message}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '&.Mui-focused fieldset': {
                    borderColor: hakikiColors.main,
                  },
                },
                '& label.Mui-focused': {
                  color: hakikiColors.main,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            {/* Role dipilih otomatis dari backend. Jika akun punya >1 role,
                dialog pemilihan role akan muncul setelah credential divalidasi. */}
            {/* Lupa Password - Hidden */}
            {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Link
                component="button"
                variant="body2"
                onClick={handleForgotPassword}
                sx={{ color: '#222', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
              >
                {t('LUPA PASSWORD?')}
              </Link>
            </Box> */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 1,
                mb: 1,
                py: 1.5,
                borderRadius: '8px',
                background: hakikiColors.main,
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                letterSpacing: 1,
                boxShadow: '0 2px 8pxrgba(255, 225, 58, 0.2)',
                border: `2px solid ${hakikiColors.main}`,
                '&:hover': {
                  background: hakikiColors.accent,
                  color: hakikiColors.main,
                  border: `2px solid ${hakikiColors.main}`,
                  boxShadow: '0 4px 16px #C8A41533',
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : t('MASUK')}
            </Button>
            {/* Google Login - Hidden */}
            {/* <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
              <Box sx={{ flex: 1, borderBottom: '1px solid #e0e0e0' }} />
              <Typography variant="body2" sx={{ px: 2, color: 'grey.500' }}>
                {t('ATAU LANJUTKAN')}
              </Typography>
              <Box sx={{ flex: 1, borderBottom: '1px solid #e0e0e0' }} />
            </Box>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLoginClick}
              sx={{
                mb: 2,
                py: 1.2,
                borderRadius: '8px',
                border: `2px solid ${hakikiColors.main}`,
                color: hakikiColors.main,
                fontWeight: 'bold',
                letterSpacing: 1,
                background: hakikiColors.accent,
                '&:hover': {
                  background: hakikiColors.main,
                  borderColor: hakikiColors.main,
                  color: hakikiColors.accent,
                  boxShadow: '0 2px 8pxrgba(255, 242, 58, 0.13)',
                },
              }}
            >
              {t('MASUK DENGAN GOOGLE')}
            </Button> */}
          </form>
        </Box>
      </Box>

      {/* Dialog: Pilih Role (muncul jika akun memiliki lebih dari satu role) */}
      <Dialog
        open={roleDialog.open}
        onClose={() => !loading && setRoleDialog({ open: false, roles: [] })}
        TransitionComponent={SlideUp}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <BadgeIcon sx={{ color: hakikiColors.main }} />
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
              {t('PILIH ROLE')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('AKUN ANDA MEMILIKI BEBERAPA ROLE. SILAKAN PILIH ROLE UNTUK MELANJUTKAN.')}
          </DialogContentText>
          <List sx={{ p: 0 }}>
            {roleDialog.roles.map((r) => (
              <ListItemButton
                key={r}
                onClick={() => handleSelectRole(r)}
                disabled={loading}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: hakikiColors.main,
                    bgcolor: 'rgba(200,164,21,0.06)',
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: roleColor(r), fontWeight: 700 }}>
                    {roleInitial(r)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={roleLabel(r)}
                  primaryTypographyProps={{ fontWeight: 600, letterSpacing: 0.3 }}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRoleDialog({ open: false, roles: [] })}
            disabled={loading}
            sx={{ color: 'text.secondary' }}
          >
            {t('BATAL')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Selamat Datang (muncul setelah login sukses) */}
      <Dialog
        open={welcomeDialog.open}
        onClose={handleWelcomeContinue}
        TransitionComponent={SlideUp}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${hakikiColors.main} 0%, ${hakikiColors.accent || '#F0C14B'} 100%)`,
            color: '#fff',
            textAlign: 'center',
            py: 3.5,
            px: 3,
          }}
        >
          <CheckCircleOutline sx={{ fontSize: 56, mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
            {t('SELAMAT DATANG')}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            {welcomeDialog.user?.fullName || welcomeDialog.user?.username}
          </Typography>
        </Box>
        <DialogContent sx={{ textAlign: 'center', pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('ANDA LOGIN SEBAGAI')}
          </Typography>
          <Chip
            label={roleLabel(welcomeDialog.user?.role)}
            sx={{
              fontWeight: 700,
              letterSpacing: 0.5,
              px: 1.5,
              py: 2.25,
              fontSize: '0.95rem',
              bgcolor: roleColor(welcomeDialog.user?.role),
              color: '#fff',
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
          <Button
            onClick={handleWelcomeContinue}
            fullWidth
            variant="contained"
            autoFocus
            sx={{
              py: 1.25,
              borderRadius: 2,
              background: hakikiColors.main,
              fontWeight: 700,
              letterSpacing: 0.5,
              '&:hover': { background: hakikiColors.accent, color: hakikiColors.main },
            }}
          >
            {t('LANJUTKAN KE DASHBOARD')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
